/**
 * Cliente de Supabase Auth.
 *
 * Este panel **no administra identidades**: no hay registro, ni hashes, ni
 * reset de password propio. Eso es la misma decision que documenta
 * `src/services/auth.py` del backend, vista desde el otro lado: Supabase emite
 * el JWT, el backend lo verifica, y la tenencia (que tienda puede ver cada uno)
 * vive en la tabla `user_store_access`, no en el token.
 *
 * Consecuencia practica para el front: del token NO se puede deducir a que
 * tiendas se tiene acceso. Hay que preguntarle al servidor. Ver
 * `src/stores/StoreAccessProvider.tsx`.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import type { AppConfig } from "../config";

/**
 * Construir el cliente.
 *
 * `persistSession` + `autoRefreshToken`: el access token de Supabase dura una
 * hora. Sin refresh automatico, el panel empezaria a recibir 401 del backend a
 * mitad de la jornada; sin persistencia, cada F5 pediria login de nuevo.
 *
 * `detectSessionInUrl`: los links de magic link / recovery vuelven con el token
 * en el fragmento de la URL y hay que consumirlo al cargar.
 */
export function createSupabaseClient(config: AppConfig): SupabaseClient {
  return createClient(config.supabaseUrl, config.supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Storage por defecto (localStorage). Es un panel interno en un dominio
      // propio; una cookie httpOnly exigiria un backend de sesion que no
      // existe (el backend es stateless y solo verifica el Bearer).
      flowType: "pkce",
    },
  });
}
